import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import PollsTab from '@/components/PollsTab';
import EventsTab from '@/components/EventsTab';
import MusicTab from '@/components/community/MusicTab';
import { CommunitySidebar, MembersTab, InvitesTab, ChatTab, ROLE_LABELS } from '@/components/community';
import {
  Users, UserPlus, Send, Plus, Church, GraduationCap,
  Crown, Loader2, ArrowLeft, Link2, Camera, BarChart3, Calendar, MessageSquare, Clock, Music
} from 'lucide-react';
import { getErrorMessage } from '../utils/errorHandler';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CommunityPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  
  const [communities, setCommunities] = useState([]);
  const [selectedCommunity, setSelectedCommunity] = useState(null);
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [joinDialogOpen, setJoinDialogOpen] = useState(false);
  const [broadcastDialogOpen, setBroadcastDialogOpen] = useState(false);
  
  // Form states
  const [newCommunity, setNewCommunity] = useState({ name: '', description: '', type: user?.user_type || 'church' });

  // Check if user can create/manage communities
  const canManageCommunities = 
    user?.user_type === 'church' || 
    user?.user_type === 'educator' ||
    user?.user_subcategory === 'pastor' ||
    user?.user_subcategory === 'admin';
  const [newInvite, setNewInvite] = useState({ role: 'member', max_uses: null, expires_in_days: 7 });
  const [joinCode, setJoinCode] = useState('');
  const [broadcastContent, setBroadcastContent] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const fetchCommunities = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/communities`, { withCredentials: true });
      setCommunities(response.data);
      if (response.data.length > 0 && !selectedCommunity) {
        setSelectedCommunity(response.data[0]);
      }
    } catch (error) {
      console.error('Failed to fetch communities:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedCommunity]);

  const fetchCommunityData = useCallback(async (communityId) => {
    try {
      const [membersRes, invitesRes, messagesRes] = await Promise.all([
        axios.get(`${API}/communities/${communityId}/members`, { withCredentials: true }),
        axios.get(`${API}/communities/${communityId}/invites`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/communities/${communityId}/messages`, { withCredentials: true })
      ]);
      setMembers(membersRes.data);
      setInvites(invitesRes.data);
      setMessages(messagesRes.data);
    } catch (error) {
      console.error('Failed to fetch community data:', error);
    }
  }, []);

  useEffect(() => {
    fetchCommunities();
  }, [fetchCommunities]);

  useEffect(() => {
    if (selectedCommunity) {
      fetchCommunityData(selectedCommunity.community_id);
    }
  }, [selectedCommunity, fetchCommunityData]);

  // Auto-refresh messages every 10 seconds
  useEffect(() => {
    if (!selectedCommunity) return;
    
    const interval = setInterval(async () => {
      try {
        const messagesRes = await axios.get(
          `${API}/communities/${selectedCommunity.community_id}/messages`, 
          { withCredentials: true }
        );
        setMessages(messagesRes.data);
      } catch (error) {
        console.error('Failed to refresh messages:', error);
      }
    }, 10000);
    
    return () => clearInterval(interval);
  }, [selectedCommunity]);

  // Handlers
  const handleCreateCommunity = async () => {
    if (!newCommunity.name.trim()) {
      toast.error('Please enter a community name');
      return;
    }
    
    setIsCreating(true);
    try {
      await axios.post(`${API}/communities`, newCommunity, { withCredentials: true });
      toast.success('Community created!');
      setCreateDialogOpen(false);
      setNewCommunity({ name: '', description: '', type: user?.user_type || 'church' });
      fetchCommunities();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create community'));
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateInvite = async () => {
    if (!selectedCommunity) return;
    
    try {
      await axios.post(
        `${API}/communities/${selectedCommunity.community_id}/invite`,
        newInvite,
        { withCredentials: true }
      );
      toast.success('Invite code created!');
      setInviteDialogOpen(false);
      fetchCommunityData(selectedCommunity.community_id);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create invite'));
    }
  };

  const handleJoinCommunity = async () => {
    if (!joinCode.trim()) {
      toast.error('Please enter an invite code');
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/communities/join`,
        { invite_code: joinCode },
        { withCredentials: true }
      );
      toast.success(`Joined ${response.data.community_name}!`);
      setJoinDialogOpen(false);
      setJoinCode('');
      fetchCommunities();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to join community'));
    }
  };

  const handleSendBroadcast = async () => {
    if (!selectedCommunity || !broadcastContent.trim()) return;
    
    try {
      await axios.post(
        `${API}/communities/${selectedCommunity.community_id}/messages`,
        { content: broadcastContent, is_broadcast: true },
        { withCredentials: true }
      );
      toast.success('Broadcast sent!');
      setBroadcastDialogOpen(false);
      setBroadcastContent('');
      fetchCommunityData(selectedCommunity.community_id);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to send broadcast'));
    }
  };

  const handleSendChatMessage = async (content) => {
    if (!selectedCommunity) return;
    
    await axios.post(
      `${API}/communities/${selectedCommunity.community_id}/messages`,
      { content, is_broadcast: false },
      { withCredentials: true }
    );
    fetchCommunityData(selectedCommunity.community_id);
  };

  const handleUpdateRole = async (userId, newRole) => {
    if (!selectedCommunity) return;
    
    try {
      await axios.put(
        `${API}/communities/${selectedCommunity.community_id}/members/role`,
        { user_id: userId, role: newRole },
        { withCredentials: true }
      );
      toast.success('Role updated!');
      fetchCommunityData(selectedCommunity.community_id);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update role'));
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!selectedCommunity) return;
    
    try {
      await axios.delete(
        `${API}/communities/${selectedCommunity.community_id}/members/${userId}`,
        { withCredentials: true }
      );
      toast.success('Member removed');
      fetchCommunityData(selectedCommunity.community_id);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to remove member'));
    }
  };

  const handleReactionChange = (messageId, newReactions) => {
    setMessages(prev => prev.map(m => 
      m.message_id === messageId ? { ...m, reactions: newReactions } : m
    ));
  };

  // Check if user has admin role in selected community OR is a pastor
  const isAdmin = selectedCommunity?.my_role === 'admin' || selectedCommunity?.my_role === 'pastor';
  const availableRoles = selectedCommunity?.type === 'church' 
    ? ['admin', 'pastor', 'worship_leader', 'music', 'multimedia', 'volunteer', 'member']
    : ['admin', 'teacher', 'assistant', 'student'];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <TooltipProvider>
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 dark:bg-slate-900/80 backdrop-blur-lg border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center">
                <Users className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-heading text-xl font-bold">{t('community')}</h1>
                <p className="text-xs text-muted-foreground">{t('manage_communities') || 'Manage your communities'}</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Join Community Dialog */}
            <Dialog open={joinDialogOpen} onOpenChange={setJoinDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="gap-2">
                  <Link2 className="w-4 h-4" />
                  {t('join') || 'Join'}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t('join_community')}</DialogTitle>
                  <DialogDescription>{t('enter_invite_code_desc') || 'Enter an invite code to join a community'}</DialogDescription>
                </DialogHeader>
                <div className="py-4">
                  <Label htmlFor="join-code">{t('invite_code')}</Label>
                  <Input
                    id="join-code"
                    placeholder="ABCD1234"
                    value={joinCode}
                    onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                    className="mt-2 font-mono text-center text-lg tracking-widest"
                    maxLength={8}
                  />
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setJoinDialogOpen(false)}>{t('cancel')}</Button>
                  <Button onClick={handleJoinCommunity}>{t('join_community')}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Create Community Dialog - visible to church, educator, and pastor users */}
            {canManageCommunities && (
              <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="gap-2 bg-amber-500 hover:bg-amber-600 text-black">
                    <Plus className="w-4 h-4" />
                    {t('create_community')}
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{t('create_community')}</DialogTitle>
                    <DialogDescription>{t('create_community_desc') || 'Create a new community for your organization'}</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>{t('community_name')}</Label>
                      <Input
                        placeholder={t('community_name_placeholder') || 'My Church / My Class'}
                        value={newCommunity.name}
                        onChange={(e) => setNewCommunity({ ...newCommunity, name: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('description') || 'Description'}</Label>
                      <Textarea
                        placeholder={t('community_description_placeholder') || 'A brief description of your community...'}
                        value={newCommunity.description}
                        onChange={(e) => setNewCommunity({ ...newCommunity, description: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('type') || 'Type'}</Label>
                      <Select 
                        value={newCommunity.type} 
                        onValueChange={(v) => setNewCommunity({ ...newCommunity, type: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="church">
                            <div className="flex items-center gap-2">
                              <Church className="w-4 h-4" />
                              Church
                            </div>
                          </SelectItem>
                          <SelectItem value="educator">
                            <div className="flex items-center gap-2">
                              <GraduationCap className="w-4 h-4" />
                              Educator
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleCreateCommunity} disabled={isCreating}>
                      {isCreating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {communities.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">{t('no_communities_yet') || 'No Communities Yet'}</h3>
              <p className="text-muted-foreground mb-4">
                {canManageCommunities
                  ? t('create_first_community') || 'Create your first community to invite members'
                  : t('join_with_code') || 'Join a community using an invite code'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-12 gap-6">
            {/* Community Sidebar */}
            <div className="col-span-12 md:col-span-3">
              <CommunitySidebar
                communities={communities}
                selectedCommunity={selectedCommunity}
                onSelectCommunity={setSelectedCommunity}
              />
            </div>

            {/* Main Content */}
            <div className="col-span-12 md:col-span-9">
              {selectedCommunity && (
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          {selectedCommunity.name}
                          {selectedCommunity.my_role === 'admin' && (
                            <Crown className="w-5 h-5 text-amber-500" />
                          )}
                        </CardTitle>
                        <CardDescription>{selectedCommunity.description || t('no_description') || 'No description'}</CardDescription>
                      </div>
                      {isAdmin && (
                        <div className="flex gap-2">
                          {/* Photo Enhancement - Church Only */}
                          {selectedCommunity.type === 'church' && (
                            <Button 
                              variant="outline" 
                              className="gap-2"
                              onClick={() => navigate(`/community/${selectedCommunity.community_id}/photos`)}
                              data-testid="photo-enhancement-btn"
                            >
                              <Camera className="w-4 h-4" />
                              Photos
                            </Button>
                          )}
                          
                          {/* Service Calendar - Church communities */}
                          {selectedCommunity?.type === 'church' && (
                            <Button 
                              variant="outline" 
                              className="gap-2"
                              onClick={() => navigate(`/community/${selectedCommunity.community_id}/calendar`)}
                              data-testid="service-calendar-btn"
                            >
                              <Calendar className="w-4 h-4" />
                              Service Calendar
                            </Button>
                          )}
                          
                          {/* Appointments - for pastors/teachers */}
                          {(selectedCommunity?.my_role === 'admin' || selectedCommunity?.my_role === 'pastor' || selectedCommunity?.my_role === 'teacher') && (
                            <Button 
                              variant="outline" 
                              className="gap-2"
                              onClick={() => navigate(`/community/${selectedCommunity.community_id}/appointments`)}
                              data-testid="appointments-btn"
                            >
                              <Clock className="w-4 h-4" />
                              Appointments
                            </Button>
                          )}
                          
                          {/* Invite Dialog */}
                          <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
                            <DialogTrigger asChild>
                              <Button variant="outline" className="gap-2">
                                <UserPlus className="w-4 h-4" />
                                Invite
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Create Invite Code</DialogTitle>
                                <DialogDescription>Generate a code to invite new members</DialogDescription>
                              </DialogHeader>
                              <div className="space-y-4 py-4">
                                <div className="space-y-2">
                                  <Label>Role to assign</Label>
                                  <Select 
                                    value={newInvite.role} 
                                    onValueChange={(v) => setNewInvite({ ...newInvite, role: v })}
                                  >
                                    <SelectTrigger>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {availableRoles.map((role) => (
                                        <SelectItem key={role} value={role}>
                                          {ROLE_LABELS[role]}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="space-y-2">
                                  <Label>Max uses (leave empty for unlimited)</Label>
                                  <Input
                                    type="number"
                                    placeholder="Unlimited"
                                    value={newInvite.max_uses || ''}
                                    onChange={(e) => setNewInvite({ 
                                      ...newInvite, 
                                      max_uses: e.target.value ? parseInt(e.target.value) : null 
                                    })}
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label>Expires in (days)</Label>
                                  <Input
                                    type="number"
                                    value={newInvite.expires_in_days || ''}
                                    onChange={(e) => setNewInvite({ 
                                      ...newInvite, 
                                      expires_in_days: e.target.value ? parseInt(e.target.value) : null 
                                    })}
                                  />
                                </div>
                              </div>
                              <DialogFooter>
                                <Button variant="outline" onClick={() => setInviteDialogOpen(false)}>Cancel</Button>
                                <Button onClick={handleCreateInvite}>Generate Code</Button>
                              </DialogFooter>
                            </DialogContent>
                          </Dialog>

                          {/* Broadcast Dialog */}
                          <Dialog open={broadcastDialogOpen} onOpenChange={setBroadcastDialogOpen}>
                            <DialogTrigger asChild>
                              <Button className="gap-2">
                                <Send className="w-4 h-4" />
                                Broadcast
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Send Broadcast</DialogTitle>
                                <DialogDescription>Send a message to all community members</DialogDescription>
                              </DialogHeader>
                              <div className="py-4">
                                <Textarea
                                  placeholder="Type your message..."
                                  value={broadcastContent}
                                  onChange={(e) => setBroadcastContent(e.target.value)}
                                  rows={5}
                                />
                              </div>
                              <DialogFooter>
                                <Button variant="outline" onClick={() => setBroadcastDialogOpen(false)}>Cancel</Button>
                                <Button onClick={handleSendBroadcast}>Send to All</Button>
                              </DialogFooter>
                            </DialogContent>
                          </Dialog>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Tabs defaultValue="members">
                      <TabsList>
                        <TabsTrigger value="members" className="gap-2">
                          <Users className="w-4 h-4" />
                          {t('members') || 'Members'} ({members.length})
                        </TabsTrigger>
                        {isAdmin && (
                          <TabsTrigger value="invites" className="gap-2">
                            <Link2 className="w-4 h-4" />
                            {t('invite_codes') || 'Invite Codes'}
                          </TabsTrigger>
                        )}
                        <TabsTrigger value="messages" className="gap-2">
                          <MessageSquare className="w-4 h-4" />
                          {t('messages')}
                        </TabsTrigger>
                        <TabsTrigger value="polls" className="gap-2">
                          <BarChart3 className="w-4 h-4" />
                          {t('polls')}
                        </TabsTrigger>
                        <TabsTrigger value="events" className="gap-2">
                          <Calendar className="w-4 h-4" />
                          {t('events')}
                        </TabsTrigger>
                        {(selectedCommunity?.type === 'church' || selectedCommunity?.type === 'education') && (
                          <TabsTrigger value="music" className="gap-2">
                            <Music className="w-4 h-4" />
                            {selectedCommunity?.type === 'church' ? 'Music' : 'School Band'}
                          </TabsTrigger>
                        )}
                      </TabsList>

                      <TabsContent value="members" className="mt-4">
                        <MembersTab
                          members={members}
                          currentUserId={user?.user_id}
                          isAdmin={isAdmin}
                          availableRoles={availableRoles}
                          communityId={selectedCommunity.community_id}
                          onUpdateRole={handleUpdateRole}
                          onRemoveMember={handleRemoveMember}
                        />
                      </TabsContent>

                      {isAdmin && (
                        <TabsContent value="invites" className="mt-4">
                          <InvitesTab invites={invites} />
                        </TabsContent>
                      )}

                      <TabsContent value="messages" className="mt-4">
                        <ChatTab
                          communityId={selectedCommunity.community_id}
                          messages={messages}
                          currentUserId={user?.user_id}
                          isAdmin={isAdmin}
                          onSendMessage={handleSendChatMessage}
                          onOpenBroadcast={() => setBroadcastDialogOpen(true)}
                          onReactionChange={handleReactionChange}
                        />
                      </TabsContent>

                      <TabsContent value="polls" className="mt-4">
                        <PollsTab 
                          communityId={selectedCommunity.community_id} 
                          isAdmin={isAdmin}
                        />
                      </TabsContent>

                      <TabsContent value="events" className="mt-4">
                        <EventsTab 
                          communityId={selectedCommunity.community_id} 
                          isAdmin={isAdmin}
                        />
                      </TabsContent>

                      {(selectedCommunity?.type === 'church' || selectedCommunity?.type === 'education') && (
                        <TabsContent value="music" className="mt-4">
                          <MusicTab 
                            communityId={selectedCommunity.community_id}
                            userRole={selectedCommunity.my_role}
                            communityType={selectedCommunity.type}
                          />
                        </TabsContent>
                      )}
                    </Tabs>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
    </TooltipProvider>
  );
};

export default CommunityPage;
